import { LoadingIndicator } from '@community_dev/pixels'
import { LoginForm } from '@community_dev/pixels'
import { useMutation } from '@tanstack/react-query'
import { useState, useEffect, ReactNode, type JSX } from 'react'
import { useTimeout } from 'react-use'
import styled from 'styled-components'

import { postLogin } from 'api/login'
import { ClientDetail } from 'components/ClientDetail'
import { useUserContext } from 'contexts/UserProvider'

type SlateLoginFormProps = {
  onTimeout: () => any
}
// TODO: use ApiError class from pixels
type LoginErrorDetails = {
  field: string
  detail: string
}

type LoginError = {
  name: string
  status: number
  errors?: [LoginErrorDetails]
}

type DashboardContext = {
  email: string
  clientId: string
  clientImage: string
  firstName: string
  lastName: string
  name?: string
  phoneNumber: string
  origin: string
}

type Message = {
  action: string
  payload?: any
}

export const StyledLink = styled.a`
  text-decoration: none;
  color: ${({ theme }) => theme?.COLORS.LINKS};
`

const dashboardUrl = import.meta.env.VITE_DASHBOARD_URL

export function SlateLoginForm({ onTimeout }: SlateLoginFormProps): JSX.Element {
  const [error, setError] = useState<string>()
  const [isMfaSetupError, setIsMfaSetupError] = useState(false)
  const [isSlateTimeout, cancel, reset] = useTimeout(1000)
  const { setToken, setClientId, clientId, setMfaToken } = useUserContext()
  const [slateContext, setSlateContext] = useState<DashboardContext | null>(null)

  useEffect(() => {
    reset()
    const handleMessage = (event: MessageEvent) => {
      if (window.opener !== event.source && event.origin !== window.origin) return
      const { data }: { data: Message } = event
      switch (data.action) {
        case 'setContext':
          cancel()
          setSlateContext(data.payload)
          setClientId(data.payload.clientId)
          break
        default:
          break
      }
    }
    window.addEventListener('message', handleMessage, false)
    window.opener?.postMessage({ action: 'ready' }, '*')
    return (): void => window.removeEventListener('message', handleMessage, false)
  }, [setClientId, cancel, reset])

  const { mutate: logIn, isLoading } = useMutation(postLogin, {
    onError(err: LoginError) {
      let message = 'Invalid password'
      if (err.name === 'ApiError' && err.status === 500) {
        message = 'Our systems are currently experiencing trouble, try again in a few seconds'
      }
      if (err.name === 'ApiError' && err.status === 409) {
        setIsMfaSetupError(true)
      }
      setError(message)
    },
    async onSuccess(rawResp) {
      const jsonBody = await rawResp.json()
      if (rawResp.status === 206) {
        setMfaToken(jsonBody.token)
      } else {
        setToken(jsonBody.token)
      }
    },
  })

  if (isSlateTimeout()) {
    return onTimeout()
  }

  if (!slateContext) return <LoadingIndicator />

  function handleSubmit({ password }) {
    if (slateContext !== null) {
      logIn({ password, email: slateContext.email, client_id: clientId })
    }
  }

  function getErrors(): ReactNode[] {
    if (isMfaSetupError) {
      return [
        <>
          To keep your account safe, we have enabled two-factor authentication. Please{' '}
          <StyledLink href={`${dashboardUrl}/logout`} rel="noreferrer" target="_blank">
            log into the Community Dashboard
          </StyledLink>{' '}
          to set up two-factor authentication.
        </>,
      ]
    } else if (error !== undefined) {
      return [error]
    } else {
      return []
    }
  }

  const name = slateContext.name || `${slateContext.firstName || ''} ${slateContext.lastName || ''}`.trim()

  return (
    <LoginForm
      errors={getErrors()}
      forgotPasswordLink={import.meta.env.VITE_DASHBOARD_URL + '/reset-password'}
      heading="Re-enter your password"
      isLoading={isLoading}
      loginButtonLabel="Submit"
      onSubmit={handleSubmit}
      userFieldView={
        <ClientDetail clientImage={slateContext.clientImage} name={name} phoneNumber={slateContext.phoneNumber} />
      }
    />
  )
}
